import * as Sentry from '@sentry/browser';
import isPlainObject from 'lodash.isplainobject';

import { PicsioAmplitudeInterface } from './Amplitude';

interface Settings {
  env: string;
  appName: Function | string;
  dsn: string;
  amplitude: PicsioAmplitudeInterface;
  release: string;
}

/** Convert any value to an Error so Sentry shows a clear message instead of <object>.value or <unknown> */
function normalizeToError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value);
  if (value === null || value === undefined) return new Error('Unknown error (null or undefined)');
  if (typeof value === 'object') {
    try {
      const msg = JSON.stringify(value);
      return new Error(msg.length > 500 ? msg.slice(0, 500) + '…' : msg);
    } catch {
      return new Error(String(value));
    }
  }
  return new Error(String(value));
}

export default class PicsioSentry {
  isEnabled: boolean;
  user: any;
  getAppName: Function;
  amplitude: PicsioAmplitudeInterface;
  constructor(settings: Settings) {
    this.isEnabled = settings.env !== 'development';
    this.user = {};
    this.getAppName =
      typeof settings.appName === 'function'
        ? settings.appName
        : () => settings.appName;
    this.amplitude = settings.amplitude;

    if (this.isEnabled) {
      const { email, displayName } = this.user;
      Sentry.init({
        dsn: settings.dsn,
        attachStacktrace: true,
        environment: settings.env,
        release: settings.release,
        sendDefaultPii: true,
        integrations: [
          Sentry.browserTracingIntegration({
            linkPreviousTrace: 'session-storage',
          }),
           Sentry.replayIntegration(),
        ],
        tracesSampleRate: 0.1,
        beforeSend(event, hint) {
          if (event.extra && event.extra.showDialog) {
            Sentry.showReportDialog({
              eventId: event.event_id,
              lang: 'en',
              user: { email, name: displayName },
            });
          }
          const error = hint?.originalException;
          if (typeof error === 'object' && error !== null && 'message' in error && (error as any).message === 'Script error.') {
            event.extra = { ...event.extra, note: 'CORS or third-party script error' };
          }
          // Normalize non-Error exceptions so Sentry shows a clear message instead of <object>.value / <unknown>
          if (error !== undefined && error !== null && !(error instanceof Error)) {
            const normalized = normalizeToError(error);
            if (event.exception?.values?.[0]) {
              event.exception.values[0].type = 'Error';
              event.exception.values[0].value = normalized.message;
            }
            if (event.message) event.message = normalized.message;
          }
          return event;
        },
        ignoreErrors:[
          "ResizeObserver loop limit exceeded", // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
          "ResizeObserver loop completed with undelivered notifications", // https://forum.sentry.io/t/node-js-filtering-with-beforesend-not-having-any-effect/8833
          "Non-Error promise rejection captured", // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/17
          // CefSharp/Outlook SafeSearch and similar bots (https://trackjs.com/javascript-errors/object-not-found-matching-id-methodname-paramcount/)
          /Object Not Found Matching Id:\d+, MethodName:/,
         ]
      });
      Sentry.diagnoseSdkConnectivity().then(result => {
        if (result) {
          console.warn('[Sentry] Connectivity issues:', result);
        } else {
          console.info('[Sentry] Connectivity OK');
        }
      });

      window.addEventListener('error', e => {
       this.send(e.error || new Error(e.message), { ErrorType: 'WindowError' });
      });

      window.addEventListener('unhandledrejection', e => {
        this.send(e.reason, { ErrorType: 'UnhandledRejectionError' });
      });
    }
  }

  setUser(user: any) {
    this.user = user;
  }

  unsetUser() {
    this.user = {};
  }

send(err: string | any, extraData = {}) {
  if (this.isEnabled) {
    Sentry.setUser({ email: this.user.email });
    Sentry.setExtra('App', this.getAppName());

    if (this.amplitude && this.amplitude.instance) {
      Sentry.setExtra('AmplitudeSessionId', this.amplitude.instance.getSessionId());
    } else {
      Sentry.setExtra(
        'AmplitudeSessionId',
        'Can not get SesstionId, adBlock/uBlock is possible'
      );
    }

    Object.entries(extraData).forEach(([key, value]) => {
      Sentry.setExtra(key, value);
    });

    if (typeof err === 'string') {
      Sentry.captureMessage(err);
    } else {
      Sentry.captureException(err instanceof Error ? err : normalizeToError(err));
    }
  }
}

  addBreadcrumb(message = 'Undefined') {
    if (isPlainObject(message)) message = JSON.stringify(message, null, 2);

    Sentry.addBreadcrumb({
      category: 'Manual',
      message,
      level: 'info',
    });
  }
}
